import { CubeQueryRecord } from '../types/cubeQueryTypes';
import { loadState, saveState } from './queryStorage';
import { getPageNameFromPath } from './componentNameExtractor';

export class QueryStore {
  private queries: CubeQueryRecord[] = [];

  private readonly maxQueries = 1000;

  private listeners = new Set<() => void>();

  private isRecording = false;

  constructor() {
    const state = loadState();
    this.isRecording = state.isRecording;
    this.queries = state.queries;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch {}
    });
  }

  persistState(): void {
    saveState({ isRecording: this.isRecording, queries: this.queries });
  }

  setRecording(recording: boolean): void {
    this.isRecording = recording;
  }

  getRecording(): boolean {
    return this.isRecording;
  }

  addQuery(record: CubeQueryRecord): void {
    if (this.queries.length < this.maxQueries) {
      this.queries.push(record);
      this.persistState();
      this.notifyListeners();
    }
  }

  addQueries(records: CubeQueryRecord[]): void {
    const available = this.maxQueries - this.queries.length;
    if (available <= 0 || records.length === 0) return;

    const toAdd = records.slice(0, available);
    this.queries.push(...toAdd);
    this.persistState();
    this.notifyListeners();
  }

  getQueries(): CubeQueryRecord[] {
    return [...this.queries];
  }

  clearQueries(): void {
    this.queries = [];
    this.persistState();
    this.notifyListeners();
  }

  deleteQuery(queryId: string): void {
    this.queries = this.queries.filter(q => q.id !== queryId);
    this.persistState();
    this.notifyListeners();
  }

  deleteQueriesByComponent(componentName: string): void {
    this.queries = this.queries.filter(q => q.componentName !== componentName);
    this.persistState();
    this.notifyListeners();
  }

  deleteQueriesByPage(pagePath: string): void {
    this.queries = this.queries.filter(q => {
      const queryPageName = getPageNameFromPath(q.pagePath);
      return queryPageName !== pagePath;
    });
    this.persistState();
    this.notifyListeners();
  }

  getMaxQueries(): number {
    return this.maxQueries;
  }

  getCurrentCount(): number {
    return this.queries.length;
  }

  updateQuery(queryId: string, updates: Partial<CubeQueryRecord>): void {
    const index = this.queries.findIndex(q => q.id === queryId);
    if (index === -1) return;

    this.queries[index] = { ...this.queries[index], ...updates };
    this.persistState();
    this.notifyListeners();
  }

  findQuery(queryId: string): CubeQueryRecord | undefined {
    return this.queries.find(q => q.id === queryId);
  }

  isFull(): boolean {
    return this.queries.length >= this.maxQueries;
  }
}
